import { RunStatus } from '../../../../../../../backend/src/types/types';
import { cn } from '../../../../lib/utils';
import { Icon } from '@/new/components/icon';
import {
  PipelineProcessingStageDescriptions,
  PipelineProcessingStages,
  ProcessingStatus,
} from '@/new/lib/pipeline-status';

type StageState = 'completed' | 'active' | 'awaiting-approval' | 'upcoming';

// Dot-grid that surrounds each card. The card itself (bg-white) masks the centre, so the
// dots only show in the padded ring around it — strongest right next to the card and
// fading out towards the edges via the radial mask. Colour is driven by the card state.
const DotGridColors: Partial<Record<StageState, string>> = {
  active: 'rgba(56, 189, 248, 0.9)',
  'awaiting-approval': 'rgba(251, 191, 36, 0.9)',
};

const DotGrid = ({ state }: { state: StageState }) => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0"
    style={{
      backgroundImage: `radial-gradient(${DotGridColors[state] ?? 'rgba(148, 163, 184, 0.55)'} 1px, transparent 1px)`,
      backgroundSize: '8px 8px',
      maskImage: 'radial-gradient(ellipse 55% 55% at center, black 70%, transparent 100%)',
      WebkitMaskImage: 'radial-gradient(ellipse 55% 55% at center, black 70%, transparent 100%)',
    }}
  />
);

const StageStatusConfig: Record<StageState, { icon: string | null; label: string; textColor: string }> = {
  completed: { icon: 'lucide--circle-check', label: 'Complete', textColor: 'text-teal-500' },
  active: { icon: 'lucide--loader animate-spin ease-in-out', label: 'In progress', textColor: 'text-sky-500' },
  'awaiting-approval': { icon: 'lucide--shield-alert', label: 'Awaiting approval', textColor: 'text-amber-500' },
  upcoming: { icon: null, label: 'Pending', textColor: 'text-gray-400' },
};

interface ProcessingPipelineProps {
  casePipelineStatus?: RunStatus | null;
  requiresAdminApproval?: boolean;
}

export const ProcessingPipeline = ({ casePipelineStatus, requiresAdminApproval = false }: ProcessingPipelineProps) => {
  const stages = PipelineProcessingStages.filter(
    (status) =>
      // Parsing has no card of its own; runs in that status display on Extracting.
      status !== RunStatus.Parsing &&
      (status !== RunStatus.AwaitingApproval || casePipelineStatus === RunStatus.AwaitingApproval),
  );

  const effectiveStatus = casePipelineStatus === RunStatus.Parsing ? RunStatus.Extracting : casePipelineStatus;
  const currentIndex = PipelineProcessingStages.indexOf(effectiveStatus as ProcessingStatus);

  const getState = (status: ProcessingStatus): StageState => {
    const stageIndex = PipelineProcessingStages.indexOf(status);
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) {
      return status === RunStatus.AwaitingApproval ? 'awaiting-approval' : 'active';
    }
    return 'upcoming';
  };

  return (
    <div className="flex flex-col items-center justify-center">
      {stages.map((status: ProcessingStatus, index: number) => {
        const isAwaitingApproval = status === RunStatus.AwaitingApproval;
        const state = getState(status);

        // Override the approval stage title if it requires admin approval
        const stageTitle =
          isAwaitingApproval && requiresAdminApproval
            ? 'Admin Approval'
            : PipelineProcessingStageDescriptions[status].title;
        const stageDescription = PipelineProcessingStageDescriptions[status].description;

        return (
          <div className="relative w-full max-w-lg p-10 py-8">
            <DotGrid state={state} />

            <div
              className={cn(
                'relative flex h-full flex-col rounded-xl border transition-all',
                state === 'active' &&
                  'border-sky-300 bg-sky-50 shadow-[0_0_32px_rgba(56,189,248,0.25)] hover:shadow-[0_0_32px_rgba(56,189,248,0.35)]',
                state === 'completed' && 'border-teal-300 bg-teal-50',
                state === 'awaiting-approval' &&
                  'border-amber-300 bg-amber-50 shadow-[0_0_32px_rgba(251,191,36,0.25)] hover:shadow-[0_0_32px_rgba(251,191,36,0.35)]',
                state === 'upcoming' && 'border-gray-200 bg-gray-100',
              )}
            >
              <div className="flex flex-col gap-1 rounded-xl bg-white p-4 px-5">
                <p className="text-sm font-semibold text-gray-800">
                  <span className="font-normal text-gray-400">{index + 1}.</span> {stageTitle}
                </p>
                <p className="text-xs leading-snug text-gray-500">{stageDescription}</p>
              </div>

              <div
                className={cn(
                  'flex items-center gap-1.5 rounded-b-xl px-4 py-2.5 text-xs font-medium',
                  state === 'active' && 'bg-sky-50',
                  state === 'completed' && 'bg-teal-50',
                  state === 'awaiting-approval' && 'bg-amber-50',
                  state === 'upcoming' && 'bg-gray-100',
                  StageStatusConfig[state].textColor,
                )}
              >
                {StageStatusConfig[state].icon && <Icon className={StageStatusConfig[state].icon} />}
                {StageStatusConfig[state].label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
